import React, { useCallback, useEffect, useState } from 'react'
import { RSSPreviewData, RSSService } from '../../generic/rssService'
import { Loading } from '../loading'
import * as GlobalTypes from '../types'
import './styles.scss'

export interface RSSFeedPreviewProps {
  source: GlobalTypes.Source | null
  isOpen: boolean
  onClose: () => void
}

export const RSSFeedPreview: React.FC<RSSFeedPreviewProps> = ({ source, isOpen, onClose }) => {
  const [previewData, setPreviewData] = useState<RSSPreviewData | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchPreview = useCallback(async () => {
    if (!source || !source.identifier || source.type !== GlobalTypes.SourceTypes.rss) {
      return
    }

    setIsLoading(true)
    try {
      const rssService = new RSSService()
      const data = await rssService.fetchRSSPreview(source.identifier)
      setPreviewData(data)
    } catch (error) {
      // Handle RSS fetch errors gracefully
      setPreviewData({
        feedInfo: {
          title: 'Error Loading Feed',
          description: 'Unable to load RSS feed',
          link: source?.identifier || '',
          lastBuildDate: new Date().toISOString(),
          items: [],
        },
        recentItems: [],
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    } finally {
      setIsLoading(false)
    }
  }, [source?.identifier, source?.type])

  useEffect(() => {
    if (isOpen && source && source.type === GlobalTypes.SourceTypes.rss) {
      fetchPreview()
    } else if (!isOpen) {
      // Reset preview data when modal closes
      setPreviewData(null)
      setIsLoading(false)
    }
  }, [isOpen, source?.identifier, source?.type, fetchPreview])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  const openLink = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  if (!isOpen) {
    return null
  }

  return (
    <div
      className="rss-preview-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rss-preview-title"
    >
      <div className="rss-preview-modal">
        <div className="rss-preview-header">
          <div className="rss-preview-title-section">
            <h2 id="rss-preview-title" className="rss-preview-title">
              RSS Feed Preview
            </h2>
            <div className="rss-preview-source-name">{source?.name || 'RSS Feed'}</div>
          </div>
          <button className="rss-preview-close" onClick={onClose} aria-label="Close preview">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <div className="rss-preview-content">
          {isLoading ? (
            <div className="rss-preview-loading">
              <Loading size="32" />
              <p>Loading RSS feed preview...</p>
            </div>
          ) : previewData?.error ? (
            <div className="rss-preview-error">
              <div className="rss-preview-error-icon">⚠️</div>
              <h3>Unable to Load RSS Feed</h3>
              <p className="rss-preview-error-message">{previewData.error}</p>
              <div className="rss-preview-error-suggestions">
                <p>
                  <strong>Possible causes:</strong>
                </p>
                <ul>
                  <li>The RSS feed URL may be incorrect or outdated</li>
                  <li>The feed may be temporarily unavailable</li>
                  <li>The feed may require authentication</li>
                  <li>There may be network connectivity issues</li>
                </ul>
              </div>
              <div className="rss-preview-error-actions">
                <button className="rss-preview-retry-button" onClick={fetchPreview}>
                  Try Again
                </button>
                <button
                  className="rss-preview-visit-source-button"
                  onClick={() => source?.identifier && openLink(source.identifier)}
                >
                  Visit Source
                </button>
              </div>
            </div>
          ) : previewData ? (
            <div className="rss-preview-success">
              {/* Feed Information */}
              <div className="rss-preview-feed-info">
                <div className="rss-preview-feed-header">
                  <h3 className="rss-preview-feed-title">{previewData.feedInfo.title}</h3>
                  <button className="rss-preview-visit-button" onClick={() => openLink(previewData.feedInfo.link)}>
                    Visit Feed →
                  </button>
                </div>
                <p className="rss-preview-feed-description">{previewData.feedInfo.description}</p>
                <div className="rss-preview-feed-meta">
                  <span className="rss-preview-feed-url">{RSSService.extractDomain(previewData.feedInfo.link)}</span>
                  {previewData.feedInfo.language && (
                    <>
                      <span className="rss-preview-separator">•</span>
                      <span className="rss-preview-feed-language">{previewData.feedInfo.language}</span>
                    </>
                  )}
                  <span className="rss-preview-separator">•</span>
                  <span className="rss-preview-feed-updated">
                    Updated {RSSService.formatDate(previewData.feedInfo.lastBuildDate)}
                  </span>
                </div>
              </div>

              {/* Recent Articles */}
              <div className="rss-preview-articles">
                <h4 className="rss-preview-articles-title">Recent Articles ({previewData.recentItems.length})</h4>
                {previewData.recentItems.length > 0 ? (
                  <div className="rss-preview-articles-list">
                    {previewData.recentItems.map((item, index) => (
                      <div
                        key={item.guid || index}
                        className="rss-preview-article"
                        onClick={() => openLink(item.link)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openLink(item.link)
                          }
                        }}
                      >
                        <div className="rss-preview-article-header">
                          <h5 className="rss-preview-article-title">{item.title}</h5>
                          <div className="rss-preview-article-meta">
                            <span className="rss-preview-article-date">{RSSService.formatDate(item.pubDate)}</span>
                            {item.author && (
                              <>
                                <span className="rss-preview-separator">•</span>
                                <span className="rss-preview-article-author">{item.author}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="rss-preview-article-description">{item.description}</p>
                        {item.category && item.category.length > 0 && (
                          <div className="rss-preview-article-categories">
                            {item.category.slice(0, 3).map((cat, catIndex) => (
                              <span key={catIndex} className="rss-preview-article-category">
                                {cat}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="rss-preview-article-link">Read more →</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rss-preview-no-articles">
                    <p>No recent articles found in this feed.</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
